import styles from "./OrderConfirmation.module.css"
import { useState, useEffect, useRef } from 'react';
import Navbar from "../../components/Navbar/NavbarPure/Navbar";
import Footer from "../../components/Footer/Footer";
import { useCartContext } from "../../context/CartContext";
import SubmitButton from "../../components/Button/SubmitButton/SubmitButton";
import team from "../../assets/images/characters/team.png"
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useOrderContext } from "../../context/OrderContext";
import { useFilterContext } from "../../context/FilterContext";
import { friendlyReason } from "../../utils/paymentReason";
import { createOrder } from "../../services/cartService";
import { useTimeModal } from "../../context/TimeModalContext";
import { useQuickLinks } from "../../hooks/useQuickLinks";
import { BACKEND_URL } from "../../services/apiConfig";
import { getAuthTokenOptional } from "../../services/authUtils";
import { error as logError } from "../../utils/logger";


const BACKEND_URL_LOCAL= "http://localhost:3000";
export { BACKEND_URL };

type VerifyResult = {
    valid: boolean;
    result: {
        payId: string;
        resultCode: number;
        resultMessage: string;
        paymentStatus?: number | string;
    };
};

type StoredPayment = {
    orderNo: string;
    originalOrderNo?: string;
    amount: number;      // minor units
    currency: string;
    status: string;      // whatever you store
    createdAt: string;
};
type CardPaymentResponse = {
    payId?: string;
    redirectUrl?: string;
    error?: string;
    csob?: unknown;
};

// Helper: treat 4/7/8 as successful (card + payment button)
const isPaid = (ps?: string | number) => ['4','7','8'].includes(String(ps ?? ''));

/**
* Checkout page.
* Contains the entire checkout and handles checkout step logic.
* Contains summary of the order.
* @returns TSX.Element
*/
const OrderConfirmationPage = () => {
    const { quickLinks, setQuickLinks } = useQuickLinks();
    const {setLastTime} = useTimeModal();
    const { orderType, orderData, resetOrderData } = useOrderContext();
    const navigate = useNavigate();
    const { t } = useTranslation(); // Translation
    const { reloadCart, clearCart, cartData } = useCartContext();
    const { unset } = useFilterContext(); // Filters
    // Keep order data saved
    const orderSnapshotRef = useRef<typeof orderData | null>(null);
    useEffect(() => {
        if (orderData && !orderSnapshotRef.current) {
            // take a shallow copy; if nested fields exist, deep copy as needed
            orderSnapshotRef.current = { ...orderData };
        }
    }, [orderData]);

    // Handle card payment redirect
    const [searchParams] = useSearchParams();
    const payId = searchParams.get('payId') || '';
    const initialStatus = searchParams.get('status') || 'unknown'; // "success" | "fail" from backend redirect
    const msg = searchParams.get('msg') || ''; // friendly message from backend if present
    // Make sure refresh doesnt recreate the order 
    const createdKey = payId ? `oc_created_${payId}` : '';
    const [orderCreated, setOrderCreated] = useState<boolean>(() =>
        createdKey ? sessionStorage.getItem(createdKey) === '1' : false
    );
    const markCreated = () => {
        if (createdKey) sessionStorage.setItem(createdKey, '1');
        setOrderCreated(true);
    };
    // Decoupled clearing guard (per payId)
    const clearedKey = payId ? `oc_cleared_${payId}` : '';
    const [orderCleared, setOrderCleared] = useState<boolean>(() =>
        clearedKey ? sessionStorage.getItem(clearedKey) === '1' : false
    );
    const markCleared = () => {
        if (clearedKey) sessionStorage.setItem(clearedKey, '1');
        setOrderCleared(true);
    };

    // Fallback when there's success without a payId (rare but safe)
    const clearedNoPayIdRef = useRef(false);

    const [status, setStatus] = useState<'success' | 'fail' | 'unknown'>(initialStatus as any);
    const [detail, setDetail] = useState<string>('');          // technical detail (kept hidden from users)
    const [reason, setReason] = useState<string>('');          // user-friendly message
    const [ctx, setCtx] = useState<StoredPayment | null>(null);

    

    // Finalize process — exactly once, only when status is success
    const [finalized, setFinalized] = useState<'idle'|'doing'|'done'|'error'>('idle');
    const [finalizeInfo, setFinalizeInfo] = useState<{invoiceNo?: string, msg?: string}>({});

    useEffect(() => {
        // Only run on success and payId present, and only once
        if (status !== 'success' || !payId || finalized !== 'idle') return;

        (async () => {
            try {
                setFinalized('doing');
                const resp = await fetch(`${BACKEND_URL}/eshop/payment_gate/api/orders/finalize`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ payId }),
                });
                const data = await resp.json();

                if (resp.ok && data.ok) {
                    setFinalizeInfo({ invoiceNo: data.invoiceNo, msg: data.alreadyProcessed ? 'Already processed' : 'Processed' });
                    setFinalized('done');
                } else {
                    setFinalizeInfo({ msg: data?.error || 'Finalize failed' });
                    setFinalized('error');
                }
            } catch (e: any) {
                setFinalizeInfo({ msg: e?.message || 'Finalize failed' });
                setFinalized('error');
            }
        })();
    }, [status, payId, finalized]);

    const saveQuickLinkForSubscriber = () => {
        if (!orderData.recipient) return; // no subscriber id

        const subscriberId = orderData.recipient;

        const name  = orderData.receiver?.trim();
        const phone = orderData.phone?.trim();
        const floor = orderData.floor;
        const room  = orderData.room?.trim();

        // nothing to save
        if (!name && !phone && floor === undefined && !room) return;

        setQuickLinks(prev => {
            const existing = prev.find(link => link.subscriber === subscriberId);

            const addUnique = <T,>(arr: T[], value: T | undefined | null) =>
                value !== undefined && value !== null && value !== "" && !arr.includes(value)
                    ? [...arr, value]
                    : arr;

            if (existing) {
                // update existing record for this subscriber
                return prev.map(link =>
                    link.subscriber === subscriberId
                        ? {
                            ...link,
                            names: addUnique(link.names, name),
                            numbers: addUnique(link.numbers, phone),
                            floors: addUnique(link.floors, floor),
                            rooms: addUnique(link.rooms, room),
                        }
                        : link
                );
            } else {
                // create a new record
                return [
                    ...prev,
                    {
                        subscriber: subscriberId,
                        names: name  ? [name]  : [],
                        numbers: phone ? [phone] : [],
                        floors: floor !== undefined ? [floor] : [],
                        rooms: room ? [room] : [],
                    },
                ];
            }
        });
    };


    // Create an order
    useEffect(() => {
        // Run only once after successful payment + finalize OK
        if (status !== 'success') return;
        if (finalized !== 'done') return;
        if (orderCreated) return;

        (async () => {
            try {
                const od = orderSnapshotRef.current;       // use the snapshot
                if (!od) return;

                const noteLines = [
                    `telefon: ${od.phone ?? ""}`,
                    `patro: ${od.floor ?? ""}`,
                    `místnost: ${od.room ?? ""}`,
                    `přebírající osoba: ${od.recipient ?? ""}`,
                    `preferovaný čas: ${od.time ?? ""}`,
                    `poznámka: ${od.note ?? ""}`,
                ];
                const note = noteLines.join('\n').trim();

                const pm = od.paymentMethod || 'card';
                setLastTime("");
                await createOrder(
                    pm,
                    od.code ?? "",
                    od.eventName ?? "",
                    od.phone,
                    od.floor !== undefined ? String(od.floor) : "",
                    od.room ?? "",
                    od.receiver,
                    od.time ?? "",
                    od.note ?? ""
                );

                saveQuickLinkForSubscriber();

                markCreated(); // prevent duplicates on reload
                //resetOrderData?.(); // clear checkout state if you want
                
            } catch (e) {
                // Don’t auto-retry to avoid duplicate orders; you can show a toast if you want
                logError('createOrder after success failed:', e);
            }
        })();
        }, [status, finalized, orderCreated, payId]);

    // Verify the payment + fetch stored context when payId exists
    useEffect(() => {
        if (!payId) return; // If this page is used for non-card methods, just skip.

        (async () => {
            try {
                // 1) Verify with the gateway via backend
                const vr = await fetch(`${BACKEND_URL}/eshop/payment_gate/api/csob-verify/${encodeURIComponent(payId)}`);
                const vdata = (await vr.json()) as VerifyResult;

                let ps = '';
                if (vdata?.valid) {
                    ps = String(vdata.result.paymentStatus ?? '');
                    setStatus(isPaid(ps) ? 'success' : 'fail');
                    setDetail(`${vdata.result.resultMessage || ''} (paymentStatus ${ps || 'n/a'})`);
                } else {
                    setDetail('Verification failed.');
                    setStatus('fail');
                }

                // 2) Fetch stored context (amount/currency/orderNo) for potential retry
                const pr = await fetch(`${BACKEND_URL}/eshop/payment_gate/api/payment-status/${encodeURIComponent(payId)}`, {
                    headers: { Authorization: `Bearer ${getAuthTokenOptional() ?? ''}` },
                });

                if (pr.ok) {
                    const pdata = (await pr.json()) as StoredPayment;
                    setCtx(pdata);
                }

                // 3) Friendly message shown to the user
                setReason(friendlyReason(msg, ps));
            } catch (e: any) {
                setDetail(`Verification error: ${e?.message || e}`);
                setReason(friendlyReason(msg, ''));
                setStatus('fail');
            }
        })();
    }, [payId, msg]);


    // Retry flow: start a NEW init using stored context
    const retryPayment = async () => {
        // We need enough context to rebuild the init call.
        // Prefer ctx from /payment-status; it has amount + currency + originalOrderNo.
        if (!ctx || !ctx.originalOrderNo) {
            alert('Retry not possible – missing payment context.');
            return;
        }

        try {
            // Call the same backend init as in Step4, but using the original cart/order id.
            const resp = await fetch(`${BACKEND_URL}/eshop/payment_gate/api/csob-init`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthTokenOptional() ?? ''}`,
                },
                body: JSON.stringify({
                    orderNo: ctx.originalOrderNo, // this is your cart id
                    // totalAmount recomputed server-side; retained only for logging.
                    totalAmount: ctx.amount, // already minor units
                    currency: ctx.currency, // e.g. "CZK"
                }),
            });

            const ct = resp.headers.get('content-type') || '';
            if (!ct.includes('application/json')) {
                const text = await resp.text().catch(() => '');
                throw new Error(`Retry init failed (${resp.status}). ${text.slice(0, 200)}`);
            }

            const data = (await resp.json()) as CardPaymentResponse;

            if (resp.ok && data.redirectUrl) {
                // New payment: will be a NEW payId if the old one was final-fail,
                // or reuse the existing one if it's still pending (tab races).
                window.location.assign(data.redirectUrl);
                return;
            }

            throw new Error(data.error || 'Retry init failed');
        } catch (e: any) {
            logError('Retry failed', e);
            alert(e?.message || 'Retry failed.');
        }
    };


    // Build a new orderId for retry label (not strictly required)
    const retryOrderId =
        ctx?.originalOrderNo
        ? `${ctx.originalOrderNo}-retry-${Date.now().toString().slice(-5)}`
        : `RETRY${Date.now()}`;

    useEffect(() => {
        //clearCart(); // optionally clear only on success
        if (unset) unset();
    }, []);






    useEffect(() => {
        // Only clear once per payId after successful finalize
        if (status !== 'success') return;
        if (finalized !== 'done') return;

        if (payId) {
        if (orderCleared) return;
        resetOrderData?.();
        markCleared();
        return;
        }

        // Fallback when no payId present but status shows success
        if (!clearedNoPayIdRef.current) {
        resetOrderData?.();
        clearedNoPayIdRef.current = true;
        }
    }, [status, finalized, payId, orderCleared, resetOrderData]);

  // Optional: safety net on unmount if user navigates away super fast
    useEffect(() => {
        return () => {
        if (status === 'success') {
            if (payId) {
            if (!orderCleared) {
                resetOrderData?.();
                markCleared();
            }
            } else if (!clearedNoPayIdRef.current) {
            resetOrderData?.();
            clearedNoPayIdRef.current = true;
            }
        }
        };
    }, [status, payId, orderCleared, resetOrderData]);

    return(
        <>
        <div className={styles.Page}>
            <div className={styles.Header}>
                <div className={styles.Headergroup}>
                    <Navbar />
                </div>
            </div>

            <div className={styles.Content}>
                <div className={styles.Avatars} style={{"backgroundImage": `url(${team})`}}></div>

                <div className={styles.Text}>
                    {!payId && (
                        <>
                        <p className={styles.Title}>
                            {orderType === "demand" ? t("confirmation.demand_title") : t("confirmation.title")}
                        </p>
                        <p className={styles.Subtitle}>{t("confirmation.signature")}</p>
                        </>
                    )}

                    {/* Redirect result block: only shows when arriving back with a payId */}
                    {payId && (
                        <div className={styles.ResultBlock}>
                            {status === 'success' ? (
                                <div className={styles.Success}>
                                    <p className={styles.Title}>{t('confirmation.paid_title')}</p>
                                    <p className={styles.Subtitle}>
                                        {/* prefer friendly msg from backend if present */}
                                        {reason || t('payment_fail.succ')}
                                    </p>
                                    {/* Optionally show invoice number once finalized */}
                                    {finalized === 'done' && finalizeInfo.invoiceNo && (
                                        <p className={styles.ResultMeta}>
                                            {t('confirmation.invoice')}: {finalizeInfo.invoiceNo}
                                        </p>
                                    )}
                                </div>
                            ) : status === 'fail' ? (
                                <div className={styles.Fail}>
                                    <p className={styles.Title}>{t('confirmation.failed_title')}</p>
                                    <p className={styles.Subtitle}>
                                        {reason || t('payment_fail.def')}
                                    </p>
                                    <div className={styles.Actions}>
                                        {ctx && (
                                            <SubmitButton text={t('buttons.retry_payment')} onClick={retryPayment} />
                                        )}
                                    </div>
                                    {/* keep technical detail hidden in UI, helpful during dev:
                                    <small className={styles.DevNote}>{detail}</small>
                                    */}
                                </div>
                            ) : (
                                <div className={styles.Pending}>
                                    <p className={styles.Title}>{t('confirmation.pending_title')}</p>
                                    <p className={styles.Subtitle}>{t('confirmation.pending_msg')}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <SubmitButton
                    text={t("buttons.back_home")}
                    onClick={() => {
                        // if (status === 'success') clearCart();
                        reloadCart();
                        navigate("/home");
                    }}
                />
            </div>

            <div className={styles.Footer}>
                <Footer/>
            </div>
        </div>
        </>
    )
}

export default OrderConfirmationPage;
